// Updated: 02-04-2026
// Changes:
// - "Created by" changed to "Scheduled by"
// - visit_type (Agent / Direct) shown in visit info section
// - agent_email displayed when visit_type is "agent"
// - Notes field removed (merged into Feedback)

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  MapPin,
  User,
  Home,
  MessageSquare,
  UserCheck,
  Users,
  History,
} from "lucide-react";

import "../../css/sitevisit/sitevisitdetails.css";
import api from "../../api/api";
import { formatDateTime } from "../../utils/datetime";

export default function SiteVisitDetails() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const [visit,    setVisit]   = useState(null);
  const [loading,  setLoading] = useState(true);

  useEffect(() => {
    const fetchVisit = async () => {
      try {
        const res = await api.get(`/sitevisits/${id}`);
        setVisit(res.data.data);
      } catch (err) {
        console.error("Failed to fetch site visit", err);
      } finally {
        setLoading(false);
      }
    };
    fetchVisit();
  }, [id]);

  if (loading) return <p className="page-loading">Loading site visit...</p>;
  if (!visit)  return <p className="page-error">Site visit not found</p>;

  const property = visit.property_id;
  const lead     = visit.lead_id;
  const isAgent  = visit.visit_type === "agent";

  return (
    <div className="site-visit-details-page">
      {/* HEADER */}
      <div className="details-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={18} /> Back
        </button>
        <h2>Buyer Visit Details</h2>
      </div>

      {/* CARD */}
      <div className="details-card">

        {/* PROPERTY */}
        <div className="details-section">
          <h3><Home size={18} /> Property</h3>
          <p className="title">{property?.propertyName || "Unknown Property"}</p>
          <div className="row muted">
            <MapPin size={14} />
            <span>{property?.location || "N/A"}</span>
          </div>
        </div>

        {/* LEAD */}
        <div className="details-section">
          <h3><User size={18} /> Lead</h3>
          <p className="title">{lead?.full_name || "Unknown Lead"}</p>
          {lead?.email && <p>{lead.email}</p>}
          {lead?.phone && <p>{lead.phone}</p>}
        </div>

        {/* VISIT INFO */}
        <div className="details-section grid">
          <div className="info-box">
            <Calendar size={16} />
            <div>
              <label>Date</label>
              <p>{visit.scheduled_date ? formatDateTime(visit.scheduled_date) : "N/A"}</p>
            </div>
          </div>

          <div className="info-box">
            <label>Status</label>
            <span className={`status ${visit.status}`}>
              {visit.status?.replace("_", " ").toUpperCase()}
            </span>
          </div>

          {/* Visit Type */}
          <div className="info-box">
            {isAgent ? <UserCheck size={16} /> : <Users size={16} />}
            <div>
              <label>Visit Type</label>
              <p>{isAgent ? "Agent Visit" : "Direct Visit"}</p>
            </div>
          </div>

          {/* Agent Email — only if agent visit */}
          {isAgent && visit.agent_email && (
            <div className="info-box">
              <UserCheck size={16} />
              <div>
                <label>Agent Email</label>
                <p>{visit.agent_email}</p>
              </div>
            </div>
          )}

        </div>

        {/* FEEDBACK — shown for completed visits */}
        {visit.feedback && (
          <div className="details-section">
            <h3><MessageSquare size={18} /> Feedback</h3>
            <p className="feedback">{visit.feedback}</p>
          </div>
        )}

        {/* REMARK — shown for non-completed visits */}
        {visit.remark && (
          <div className="details-section">
            <h3><MessageSquare size={18} /> Remarks</h3>
            <p className="feedback">{visit.remark}</p>
          </div>
        )}

        {/* ACTIVITY LOG */}
        {Array.isArray(visit.activityLog) && visit.activityLog.length > 0 && (
          <div className="details-section">
            <h3><History size={18} /> Activity Log</h3>
            <ul className="sv-activity">
              {[...visit.activityLog]
                .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))
                .map((e, i) => (
                  <li key={i} className="sv-activity-item">
                    <div className="sv-activity-main">
                      <strong style={{ textTransform: "capitalize" }}>
                        {e.action === "created"
                          ? "Created"
                          : e.action === "status_changed"
                          ? "Status changed"
                          : "Updated"}
                      </strong>
                      {e.field && e.action !== "created" && (
                        <span className="sv-activity-field"> · {e.field}</span>
                      )}
                      {e.action !== "created" && (e.oldValue || e.newValue) && (
                        <span className="sv-activity-change">
                          {" "}{e.oldValue || "—"} → {e.newValue || "—"}
                        </span>
                      )}
                    </div>
                    <div className="sv-activity-meta">
                      {e.changedByName || e.changedByEmail || "Unknown"} ·{" "}
                      {e.changedAt ? formatDateTime(e.changedAt) : ""}
                    </div>
                  </li>
                ))}
            </ul>
          </div>
        )}

        {/* META — "Scheduled by" instead of "Created by" */}
        {visit.createdByEmail && (
          <div className="details-section muted small">
            Scheduled by: <strong>{visit.createdByEmail}</strong>
          </div>
        )}

      </div>
    </div>
  );
}